import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { CartProvider } from './context/CartContext';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layout/AdminLayout';

// Common Components
import ProtectedRoute from './components/common/ProtectedRoute';

// Pages
import HomePage from './pages/home/HomePage';
import NotFoundPage from './pages/NotFoundPage';

// Obituary Pages
import ObituaryListPage from './pages/obituary/ObituaryListPage';
import ObituaryDetailPage from './pages/obituary/ObituaryDetailPage';
import ObituarySearchPage from './pages/obituary/ObituarySearchPage';
import ObituaryCreateStep1 from './pages/obituary/ObituaryCreateStep1';
import ObituaryCreateStep2 from './pages/obituary/ObituaryCreateStep2';
import ObituaryCreateStep3 from './pages/obituary/ObituaryCreateStep3';
import ObituaryCreatePC from './pages/obituary/ObituaryCreatePC';
import ObituaryEditPage from './pages/obituary/ObituaryEditPage';
import ObituarySharePage from './pages/obituary/ObituarySharePage';
import ObituaryRequestPage from './pages/obituary/ObituaryRequestPage';

// Wreath Pages
import WreathListPage from './pages/wreath/WreathListPage';
import WreathDetailPage from './pages/wreath/WreathDetailPage';
import CartPage from './pages/wreath/CartPage';
import CheckoutPage from './pages/wreath/CheckoutPage';
import OrderCompletePage from './pages/wreath/OrderCompletePage';
import OrderSearchPage from './pages/wreath/OrderSearchPage';

// Support Pages
import SupportMainPage from './pages/support/SupportMainPage';
import NoticeListPage from './pages/support/NoticeListPage';
import NoticeDetailPage from './pages/support/NoticeDetailPage';
import FAQPage from './pages/support/FAQPage';
import InquiryCreatePage from './pages/support/InquiryCreatePage';
import InquiryListPage from './pages/support/InquiryListPage';
import GuidePage from './pages/support/GuidePage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';

// MyPage Pages
import MyPageLayout from './pages/mypage/MyPageLayout';
import ProfilePage from './pages/mypage/ProfilePage';
import MyObituariesPage from './pages/mypage/MyObituariesPage';
import MyOrdersPage from './pages/mypage/MyOrdersPage';
import FavoritesPage from './pages/mypage/FavoritesPage';

// Legal Pages
import CompanyInfoPage from './pages/legal/CompanyInfoPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsPage from './pages/legal/TermsPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import ObituaryManagePage from './pages/admin/ObituaryManagePage';
import WreathManagePage from './pages/admin/WreathManagePage';
import WreathFormPage from './pages/admin/WreathFormPage';
import OrderManagePage from './pages/admin/OrderManagePage';
import UserManagePage from './pages/admin/UserManagePage';
import NoticeManagePage from './pages/admin/NoticeManagePage';
import InquiryManagePage from './pages/admin/InquiryManagePage';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <CartProvider>
            <Routes>
              {/* Public Routes with MainLayout */}
              <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />

              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />

              {/* Obituary Routes */}
              <Route path="/obituaries" element={<ObituaryListPage />} />
              <Route path="/obituaries/search" element={<ObituarySearchPage />} />
              <Route path="/obituaries/request" element={<ObituaryRequestPage />} />
              <Route path="/obituaries/create" element={<ObituaryCreateStep1 />} />
              <Route path="/obituaries/create/step2" element={<ObituaryCreateStep2 />} />
              <Route path="/obituaries/create/step3" element={<ObituaryCreateStep3 />} />
              <Route path="/obituaries/create/pc" element={<ObituaryCreatePC />} />
              <Route path="/obituaries/:id" element={<ObituaryDetailPage />} />
              <Route path="/obituaries/:id/edit" element={<ObituaryEditPage />} />
              <Route path="/obituaries/:id/share" element={<ObituarySharePage />} />

              {/* Wreath Routes */}
              <Route path="/wreaths" element={<WreathListPage />} />
              <Route path="/wreaths/:id" element={<WreathDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-complete" element={<OrderCompletePage />} />
              <Route path="/order-search" element={<OrderSearchPage />} />

              {/* Support Routes */}
              <Route path="/support" element={<SupportMainPage />} />
              <Route path="/support/notices" element={<NoticeListPage />} />
              <Route path="/support/notices/:id" element={<NoticeDetailPage />} />
              <Route path="/support/faq" element={<FAQPage />} />
              <Route path="/support/inquiry" element={<InquiryCreatePage />} />
              <Route path="/support/inquiries" element={<InquiryListPage />} />
              <Route path="/support/guide" element={<GuidePage />} />

              {/* MyPage Routes (Protected) */}
              <Route path="/mypage" element={
                <ProtectedRoute>
                  <MyPageLayout />
                </ProtectedRoute>
              }>
                <Route index element={<ProfilePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="obituaries" element={<MyObituariesPage />} />
                <Route path="orders" element={<MyOrdersPage />} />
                <Route path="favorites" element={<FavoritesPage />} />
              </Route>

              {/* Legal Routes */}
              <Route path="/legal/company" element={<CompanyInfoPage />} />
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />

              {/* Admin Login (No Layout) */}
              <Route path="/admin/login" element={<AdminLoginPage />} />

              {/* Admin Routes (Protected - Admin Only) */}
              <Route path="/admin/*" element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout>
                    <Routes>
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route path="obituaries" element={<ObituaryManagePage />} />
                      <Route path="wreaths" element={<WreathManagePage />} />
                      <Route path="wreaths/new" element={<WreathFormPage />} />
                      <Route path="wreaths/edit/:id" element={<WreathFormPage />} />
                      <Route path="orders" element={<OrderManagePage />} />
                      <Route path="users" element={<UserManagePage />} />
                      <Route path="notices" element={<NoticeManagePage />} />
                      <Route path="inquiries" element={<InquiryManagePage />} />
                    </Routes>
                  </AdminLayout>
                </ProtectedRoute>
              } />

              {/* 404 Not Found */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </CartProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
